/**
 * On-demand access to image capture (`react-native-view-shot`, plus `html2canvas` on web).
 *
 * ## Why this exists
 *
 * Every consumer calls these from an async, user-triggered handler — saving a card,
 * exporting a chart, downloading an edited image. None of them need capture at module
 * scope. But importing it statically pinned **~202 KB** into the web first-load bundle,
 * and almost all of that was transitive rather than the library itself:
 * `react-native-view-shot` is 4.8 KB, while its web implementation statically imports
 * `html2canvas` at ~197 KB.
 *
 * ## Why a dynamic import specifically
 *
 * Metro collects `require('literal')` during **resolution**, before dead-code
 * elimination, so an inline `const x = () => require('html2canvas')` defers *execution*
 * while still bundling the bytes — the trap the previous `cardImageExport.js` fell into,
 * comment claiming laziness and all. Only `import()` emits a separate chunk.
 *
 * ## Why one boundary and not two
 *
 * The whole payload sits behind a single `import()` of `./captureImpl`, whose platform
 * variants decide what that means. Deferring `react-native-view-shot` and `html2canvas`
 * separately does not work: `html2canvas` would be shared by two async chunks and Metro
 * hoists shared modules into the common parent, putting it straight back into entry.
 * See `captureImpl.web.js` for the full explanation.
 *
 * Native is unaffected in practice: Metro serves `import()` for native builds out of the
 * same bundle, so this is a promise wrapper rather than a network fetch there.
 */

let implPromise = null;

// Cached so repeated captures reuse one chunk fetch. Failures are not cached, so a
// capture attempted after a dropped connection can succeed on retry.
function loadImpl() {
  if (!implPromise) {
    implPromise = import('./captureImpl').catch((err) => {
      implPromise = null;
      throw err;
    });
  }
  return implPromise;
}

/**
 * Captures a view/DOM node as an image. Same signature and semantics as `captureRef`
 * from `react-native-view-shot`; only the module load is deferred, and every existing
 * call site already awaited it.
 */
export async function captureRef(node, options) {
  const { captureRef: capture } = await loadImpl();
  if (typeof capture !== 'function') {
    throw new Error('react-native-view-shot: captureRef is unavailable');
  }
  return capture(node, options);
}

/** Release only the temporary file owned by a completed native capture. */
export async function releaseCapture(uri) {
  const { releaseCapture: release } = await loadImpl();
  if (typeof release === 'function') await release(uri);
}

/**
 * Returns `html2canvas` for callers that need the library directly rather than through
 * `captureRef` — currently only card export, which relies on its `onclone` hook.
 * Web only; native resolves this to null and throws, which no native path reaches.
 */
export async function loadHtml2Canvas() {
  const { html2canvas } = await loadImpl();
  if (typeof html2canvas !== 'function') {
    throw new Error('html2canvas is not available on this platform');
  }
  return html2canvas;
}
